import { NotificationProviderSettings, PhoneOtpProvider } from 'apis/notification-providers';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  WhiteCard,
} from 'design-system';
import {
  moveRow,
  PhoneOrderRow,
  phoneOtpProviderLabels,
  sameOrder,
  toPhoneOrderRows,
  toProviders,
} from 'pages/notification-providers/utils/provider-order';
import { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { SettingSourceText } from './setting-source-text';

interface PhoneOtpProvidersCardProps {
  settings: NotificationProviderSettings['phoneOtp'];
  canUpdate: boolean;
  isSaving: boolean;
  onSave: (providers: PhoneOtpProvider[] | null) => void;
}

/** Which providers send sign-in codes by phone, primary first. Each resend takes the next one. */
export const PhoneOtpProvidersCard = ({ settings, canUpdate, isSaving, onSave }: PhoneOtpProvidersCardProps) => {
  const known = useMemo(() => settings.available.map((provider) => provider.id), [settings.available]);
  const configured = useMemo(
    () => new Set(settings.available.filter((provider) => provider.configured).map((provider) => provider.id)),
    [settings.available]
  );
  const [rows, setRows] = useState<PhoneOrderRow[]>(() => toPhoneOrderRows(settings.providers, known));
  // Start the draft over when the server's order changes (after a save or a reset), never otherwise.
  const savedKey = `${settings.providers.join(',')}|${known.join(',')}`;
  const [draftOf, setDraftOf] = useState(savedKey);
  if (draftOf !== savedKey) {
    setDraftOf(savedKey);
    setRows(toPhoneOrderRows(settings.providers, known));
  }

  const draft = toProviders(rows);
  const hasConfigured = draft.some((id) => configured.has(id));
  const isChanged = !sameOrder(draft, settings.providers);
  const toggle = (index: number) =>
    setRows(rows.map((row, at) => (at === index ? { ...row, included: !row.included } : row)));

  let position = 0;
  return (
    <WhiteCard p='lg'>
      <Stack spacing='md'>
        <Box>
          <Text fontWeight='semibold' fontSize='lg'>
            Sign-in codes by phone
          </Text>
          <Text fontSize='sm' color='gray.500'>
            The first provider sends the code. Each resend uses the next one, and a provider that fails falls back to
            the others. A provider that is not ticked is never used.
          </Text>
        </Box>

        <Stack spacing='0' border='1px solid' borderColor='gray.200' borderRadius='base'>
          {rows.map((row, index) => {
            if (row.included) position += 1;
            return (
              <Flex
                key={row.id}
                align='center'
                gap='sm'
                px='md'
                py='sm'
                borderTop={index === 0 ? undefined : '1px solid'}
                borderColor='gray.200'>
                <Text w='24px' fontWeight='semibold' color={row.included ? 'gray.700' : 'gray.300'}>
                  {row.included ? position : '–'}
                </Text>
                <Checkbox isChecked={row.included} isDisabled={!canUpdate} onChange={() => toggle(index)} flex={1}>
                  {phoneOtpProviderLabels[row.id]}
                </Checkbox>
                {!configured.has(row.id) && <Badge colorScheme='orange'>Not configured on the server</Badge>}
                {canUpdate && (
                  <HStack spacing='xs'>
                    <IconButton
                      aria-label={`Move ${phoneOtpProviderLabels[row.id]} up`}
                      size='sm'
                      variant='ghost'
                      icon={<Icon as={FiChevronUp} />}
                      isDisabled={index === 0}
                      onClick={() => setRows(moveRow(rows, index, -1))}
                    />
                    <IconButton
                      aria-label={`Move ${phoneOtpProviderLabels[row.id]} down`}
                      size='sm'
                      variant='ghost'
                      icon={<Icon as={FiChevronDown} />}
                      isDisabled={index === rows.length - 1}
                      onClick={() => setRows(moveRow(rows, index, 1))}
                    />
                  </HStack>
                )}
              </Flex>
            );
          })}
        </Stack>

        {canUpdate && !hasConfigured && (
          <Alert status='warning' borderRadius='base'>
            <AlertIcon />
            <AlertDescription>Tick at least one provider that is configured on the server.</AlertDescription>
          </Alert>
        )}

        <SettingSourceText
          source={settings.source}
          envSettingName='NOTIFICATIONS_PHONE_OTP_PROVIDERS'
          updatedAt={settings.updatedAt}
          updatedBy={settings.updatedBy}
        />

        {canUpdate && (
          <Flex justify='flex-end' gap='sm' wrap='wrap'>
            {settings.source === 'cms' && (
              <Button size='sm' variant='outline' isDisabled={isSaving} onClick={() => onSave(null)}>
                Use the server setting
              </Button>
            )}
            <Button
              size='sm'
              colorScheme='red'
              isLoading={isSaving}
              isDisabled={!isChanged || !hasConfigured}
              onClick={() => onSave(draft)}>
              Save order
            </Button>
          </Flex>
        )}
      </Stack>
    </WhiteCard>
  );
};
